import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, MapPin, Play } from "lucide-react";
import { HOME, HERO_VIDEO, UPCOMING_EVENTS, GALLERY_IMAGES, ABOUT } from "../lib/data";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const Home = () => {
  // Pick 5 photos for the merged-collage section
  const collage = [
    GALLERY_IMAGES[5],
    GALLERY_IMAGES[20],
    GALLERY_IMAGES[12],
    GALLERY_IMAGES[27],
    GALLERY_IMAGES[2],
  ];

  // Hero cross-fade between real event photos
  const heroFrames = HERO_VIDEO.rotation || [HERO_VIDEO.posterImage];
  const [activeFrame, setActiveFrame] = useState(0);
  useEffect(() => {
    if (heroFrames.length < 2) return;
    const id = setInterval(
      () => setActiveFrame((i) => (i + 1) % heroFrames.length),
      4800
    );
    return () => clearInterval(id);
  }, [heroFrames.length]);

  return (
    <div data-testid="home-page">
      {/* HERO — full-frame video placeholder */}
      <section
        className="relative h-[92vh] min-h-[640px] -mt-20 overflow-hidden grain"
        data-testid="hero-section"
      >
        <div className="absolute inset-0">
          {HERO_VIDEO.src ? (
            <video
              src={HERO_VIDEO.src}
              poster={HERO_VIDEO.posterImage}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              data-testid="hero-video"
            />
          ) : (
            <AnimatePresence mode="sync">
              {heroFrames.map((src, idx) =>
                idx === activeFrame ? (
                  <motion.img
                    key={src}
                    src={src}
                    alt="Workin Chix Events — Bay Area market"
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1.0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      opacity: { duration: 1.4, ease: "easeInOut" },
                      scale: { duration: 6.2, ease: "linear" },
                    }}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: "brightness(1.08) saturate(1.12) contrast(1.03)" }}
                    data-testid={idx === 0 ? "hero-poster" : `hero-frame-${idx}`}
                  />
                ) : null
              )}
            </AnimatePresence>
          )}
          {/* Soft top vignette so the header reads, plus a strong bottom band for guaranteed text contrast */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#231F1E]/45 to-transparent" />
          <div
            className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none"
            style={{
              background:
                "linear-gradient(to top, rgba(35,31,30,0.92) 0%, rgba(35,31,30,0.78) 28%, rgba(35,31,30,0.45) 55%, rgba(35,31,30,0) 100%)",
            }}
          />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-16 md:pb-24 text-[#EDE6DA]" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.55)" }}>
          <motion.div {...fadeUp}>
            <p
              className="tracking-overline text-xs md:text-sm font-medium mb-6"
              style={{ color: "#D6B66B", textShadow: "0 2px 14px rgba(0,0,0,0.7)" }}
              data-testid="hero-eyebrow"
            >
              {HOME.heroEyebrow}
            </p>
            <h1
              className="font-display text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.95] max-w-5xl"
              data-testid="hero-title"
            >
              {HOME.heroTitle}
            </h1>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <Link
                to="/markets"
                className="btn-primary !bg-[#EDE6DA] !text-[#231F1E] !border-[#EDE6DA] hover:!bg-[#D6B66B] hover:!border-[#D6B66B]"
                data-testid="hero-cta-markets"
              >
                {HOME.ctaShop} <ArrowUpRight size={16} />
              </Link>
              <a
                href="https://forms.gle/k5X22jtCcHQh2fZi6"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary !border-[#EDE6DA]/80 !text-[#EDE6DA] hover:!bg-[#EDE6DA] hover:!text-[#231F1E]"
                data-testid="hero-cta-vendors"
              >
                {HOME.ctaVendor}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Tiny video-play badge (decorative — for when client uploads video) */}
        <div className="absolute top-28 right-6 md:right-12 z-10 flex items-center gap-2 text-[#EDE6DA]/80 text-[10px] tracking-overline">
          <span className="w-8 h-8 rounded-full border border-[#EDE6DA]/50 flex items-center justify-center">
            <Play size={12} fill="currentColor" />
          </span>
          Reel
        </div>
      </section>

      {/* Marquee tagline strip */}
      <section className="bg-[#231F1E] text-[#EDE6DA] py-5 overflow-hidden border-y border-[#9A7456]/30">
        <div className="flex animate-marquee whitespace-nowrap">
          {Array(2)
            .fill(0)
            .map((_, n) => (
              <div key={n} className="flex shrink-0">
                {[
                  "Curated Pop-Up Markets",
                  "Bay Area",
                  "Vendors · Makers · Small Business",
                  "Est. Workin Chix",
                  "Shop Local",
                ].map((t, i) => (
                  <span
                    key={`${n}-${i}`}
                    className="font-display text-2xl md:text-3xl px-10 italic"
                  >
                    {t} <span className="text-[#D6B66B]">✦</span>
                  </span>
                ))}
              </div>
            ))}
        </div>
      </section>

      {/* MERGED PHOTO COLLAGE — Lacoste heritage style */}
      <section
        className="relative px-6 md:px-12 lg:px-16 py-32 md:py-48"
        data-testid="collage-section"
      >
        <div className="grid grid-cols-12 gap-4 md:gap-6 lg:gap-8 relative">
          <motion.div
            {...fadeUp}
            className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 lg:self-start"
          >
            <p className="tracking-overline text-xs text-[#9A7456] mb-6">
              The Manifesto
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#231F1E]">
              {HOME.manifesto}
            </h2>
            <Link
              to="/about"
              className="link-underline font-body tracking-overline text-xs mt-10 inline-block text-[#231F1E]"
              data-testid="manifesto-about-link"
            >
              About Workin Chix
            </Link>
          </motion.div>

          <div className="col-span-12 lg:col-span-7 relative">
            <div className="grid grid-cols-6 gap-4">
              <motion.div
                {...fadeUp}
                className="col-span-4 aspect-[4/5] media-zoom -mt-12"
              >
                <img src={collage[0]?.src} alt={collage[0]?.alt} className="w-full h-full object-cover" />
              </motion.div>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.9, delay: 0.1 }}
                className="col-span-2 aspect-[3/4] media-zoom mt-20"
              >
                <img src={collage[1]?.src} alt={collage[1]?.alt} className="w-full h-full object-cover" />
              </motion.div>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="col-span-2 aspect-square media-zoom -mt-6"
              >
                <img src={collage[2]?.src} alt={collage[2]?.alt} className="w-full h-full object-cover" />
              </motion.div>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.9, delay: 0.3 }}
                className="col-span-4 aspect-[16/10] media-zoom"
              >
                <img src={collage[3]?.src} alt={collage[3]?.alt} className="w-full h-full object-cover" />
              </motion.div>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.9, delay: 0.4 }}
                className="col-span-6 aspect-[16/7] media-zoom"
              >
                <img src={collage[4]?.src} alt={collage[4]?.alt} className="w-full h-full object-cover" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* UPCOMING MARKETS TEASER */}
      <section
        className="bg-[#231F1E] text-[#EDE6DA] py-24 md:py-32 px-6 md:px-12 lg:px-16"
        data-testid="markets-teaser"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="tracking-overline text-xs text-[#D6B66B] mb-5">
              Upcoming Markets
            </p>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-3xl">
              Find your next Saturday.
            </h2>
          </div>
          <Link
            to="/markets"
            className="btn-secondary !border-[#EDE6DA] !text-[#EDE6DA] hover:!bg-[#EDE6DA] hover:!text-[#231F1E] self-start md:self-end"
            data-testid="see-all-markets"
          >
            See all markets <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {UPCOMING_EVENTS.map((e, i) => (
            <motion.div
              key={e.id}
              {...fadeUp}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              className="group"
              data-testid={`home-event-card-${e.id}`}
            >
              <Link to="/markets" className="block">
                <div className="aspect-[4/5] overflow-hidden media-zoom mb-5">
                  <img src={e.cover} alt={e.name} className="w-full h-full object-cover" />
                </div>
                <p className="tracking-overline text-[10px] text-[#D6B66B] mb-2">
                  {e.status}
                </p>
                <h3 className="font-display text-2xl md:text-3xl leading-tight">
                  {e.name}
                </h3>
                <div className="mt-3 flex items-center gap-4 text-xs text-[#EDE6DA]/75">
                  <span className="flex items-center gap-1.5">
                    <Calendar size={12} /> {e.date}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-xs text-[#EDE6DA]/75">
                  <MapPin size={12} /> {e.location}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* THREE PATHWAYS */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <motion.div {...fadeUp} className="max-w-3xl mb-16">
          <p className="tracking-overline text-xs text-[#9A7456] mb-5">
            Three ways in
          </p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">
            However you arrive — there's a door for you.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#231F1E]/15">
          {[
            {
              eyebrow: "Shoppers",
              title: "Come spend a Saturday.",
              copy: "Find dates, locations and the vendors you'll meet.",
              cta: "Upcoming Markets",
              to: "/markets",
              testid: "pathway-shoppers",
            },
            {
              eyebrow: "Vendors",
              title: "Apply to sell with us.",
              copy: "Curated markets for makers, small businesses & creatives.",
              cta: "Vendor Application",
              to: "https://forms.gle/k5X22jtCcHQh2fZi6",
              external: true,
              testid: "pathway-vendors",
            },
            {
              eyebrow: "Partners",
              title: "Host with us.",
              copy: "Venues, brands and collaborators — we'd love to talk.",
              cta: "Get in touch",
              to: "/contact",
              testid: "pathway-partners",
            },
          ].map((p) => {
            const cardClassName =
              "bg-[#EDE6DA] p-10 md:p-12 group hover:bg-[#E5DEC9] transition-colors";
            const content = (
              <>
                <p className="tracking-overline text-[10px] text-[#9A7456] mb-6">
                  {p.eyebrow}
                </p>
                <h3 className="font-display text-3xl md:text-4xl leading-tight max-w-xs">
                  {p.title}
                </h3>
                <p className="font-body text-sm text-[#231F1E]/70 mt-4 max-w-xs leading-relaxed">
                  {p.copy}
                </p>
                <span className="inline-flex items-center gap-2 mt-8 tracking-overline text-[11px] text-[#231F1E] link-underline">
                  {p.cta} <ArrowUpRight size={14} />
                </span>
              </>
            );

            return p.external ? (
              <a
                key={p.title}
                href={p.to}
                target="_blank"
                rel="noopener noreferrer"
                className={cardClassName}
                data-testid={p.testid}
              >
                {content}
              </a>
            ) : (
              <Link
                key={p.title}
                to={p.to}
                className={cardClassName}
                data-testid={p.testid}
              >
                {content}
              </Link>
            );
          })}
        </div>
      </section>

      {/* About excerpt */}
      <section className="bg-[#9A7456] text-[#EDE6DA] py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <motion.div {...fadeUp} className="max-w-4xl">
          <p className="tracking-overline text-xs text-[#EDE6DA]/70 mb-6">
            About
          </p>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1]">
            {ABOUT.headline}
          </h2>
          <Link
            to="/about"
            className="btn-secondary !border-[#EDE6DA] !text-[#EDE6DA] hover:!bg-[#EDE6DA] hover:!text-[#9A7456] mt-10"
            data-testid="home-about-cta"
          >
            Read our story
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default Home;
